import { useGlobalState } from 'context/GlobalStateContext'
import {
  ProjectFdcNotificationEventsQuery,
  ProjectFdcNotificationEventsQueryResult,
  useProjectFdcNotificationEventsQuery,
} from 'schema'
import { DefaultQueryHookOptions } from 'utils/defaults'
import { createStrictContext } from 'utils/createContext'
import {
  UseRecipientsFn,
  useRecipientsFn,
} from 'components/CIMSNotifications/hooks/useRecipientsFn'
import { FDCNotificationEvent, FDCNotificationEventItem } from 'shared'

type Query = ProjectFdcNotificationEventsQuery['project']
export type FDCNotification = Query['fdcNotifications'][0]
export type Group = Query['fdcApprovalGroups'][0] &
  Query['fdcConcurrenceGroups'][0]

interface FDCNotificationsProviderContext
  extends Pick<ProjectFdcNotificationEventsQueryResult, 'refetch'> {
  data?: Query
  fetching: ProjectFdcNotificationEventsQueryResult['loading']
  recipients: UseRecipientsFn<FDCNotification['recipients']>
  concurrerEvents: Readonly<FDCNotificationEventItem>[]
  approverEvents: Readonly<FDCNotificationEventItem>[]
}

const { Provider, useContextFn } =
  createStrictContext<FDCNotificationsProviderContext>(
    'FDCNotificationsContext'
  )

export const useFDCNotificationsContext = useContextFn

const FDCNotificationsProvider: React.FC = ({ children }) => {
  const {
    state: { projectGlobalId: projectId },
  } = useGlobalState()
  const {
    data,
    loading: fetching,
    refetch,
  } = useProjectFdcNotificationEventsQuery({
    ...DefaultQueryHookOptions,
    skip: !projectId,
    variables: { globalId: projectId! },
  })
  const recipients = useRecipientsFn({
    data,
    getter: (id, { project: { fdcNotifications } }) =>
      fdcNotifications.find(
        ({ notificationEventId }) => notificationEventId === id
      )?.recipients,
    fallback: {
      __typename: 'FDCNotificationRecipients',
      every: null,
      byApprovalGroup: [],
      byConcurrenceGroup: [],
      byConcurrenceLevel: [],
      byConcurrenceLevelAndApprovalGroup: [],
      byConcurrenceLevelAndConcurrenceGroup: [],
    },
  })
  const concurrerEvents = [
    FDCNotificationEvent.Submitted,
    FDCNotificationEvent.ConcurrenceLevelRejected,
  ]
  const approverEvents = [FDCNotificationEvent.PendingApprovals]
  return (
    <Provider
      value={{
        data: data?.project,
        fetching,
        refetch,
        recipients,
        concurrerEvents,
        approverEvents,
      }}
    >
      {children}
    </Provider>
  )
}

export default FDCNotificationsProvider
